import ArtistCards from "@/components/ArtistCards";
import Loading from "@/components/Loading";
import { apiUrl } from "@/constants";
import { Artist } from "@/types";
import {
  Box,
  Center,
  Container,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import { QueryClient, dehydrate, useQuery } from "@tanstack/react-query";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";

export default function ArtistPage() {
  const { colorMode } = useColorMode();
  const backgroundColor = colorMode === "light" ? "white" : "black";
  const { isOpen, onClose } = useDisclosure();

  const router = useRouter();
  const { slug } = router.query;

  const getArtist = async () => {
    const res = await fetch(`${apiUrl}/artists/${slug}/`);
    const data: Artist = await res.json();
    return data;
  };

  const {
    data: artist,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["artist", slug],
    queryFn: getArtist,
    enabled: !!slug,
  });

  if (isLoading) return <Loading />;
  if (isError || !artist) return <Center>Artist not found.</Center>;

  return (
    <div>
      <Head>
        <title>{`${artist.name} is on Songcards`}</title>
        <link rel="icon" href="/songcards.png" />
        <link rel="apple-touch-icon" href="/icon.png" />
        <meta name="description" content="Listen | Discover | Collect" />
        <link
          rel="canonical"
          href={`https://songcards.io/artists/${artist.slug}`}
        />
        <meta itemProp="name" content={`${artist.name} is on Songcards`} />
        <meta itemProp="description" content="Listen | Discover | Collect" />
        <meta itemProp="image" content={`${artist.profile_image ?? ""}`} />
        <meta property="og:title" content={`${artist.name} is on Songcards`} />
        <meta property="og:description" content="Listen | Discover | Collect" />
        <meta property="og:image" content={`${artist.profile_image ?? ""}`} />
        <meta
          property="og:url"
          content={`https://songcards.io/artists/${artist.slug}`}
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${artist.name} is on Songcards`} />
        <meta
          name="twitter:description"
          content="Listen | Discover | Collect"
        />
        <meta name="twitter:image" content={`${artist.profile_image ?? ""}`} />
      </Head>
      <Container mb={200} maxW="2xl">
        <Center>
          <Heading
            as="h1"
            maxW="sm"
            mt={2}
            mr={0}
            minW={100}
            mb={5}
            size="xl"
            textAlign="center"
          >
            {artist.name}
          </Heading>
        </Center>
        <Center>
          <Text mb={10}>is on Songcards</Text>
        </Center>
        <Modal isOpen={isOpen} onClose={onClose} motionPreset="slideInBottom">
          <ModalOverlay
            backdropInvert="80%"
            backdropFilter="blur(10px) hue-rotate(10deg)"
          />
          <ModalContent backgroundColor={backgroundColor} maxW={300}>
            <ModalHeader>About {artist.name}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={5}>{artist.about}</Text>
            </ModalBody>
          </ModalContent>
        </Modal>
        <Container mb={0}></Container>
        <ArtistCards cards={artist.cards} />
        <Box mt={10} mb={300}>
          <Link href={`/`}>
            <Center>
              <Image
                src={
                  colorMode === "dark"
                    ? "/SC Icon_B_White.svg"
                    : "/SC Icon_B_Black.svg"
                }
                width={30}
                height={500}
                alt="Discover more Songcards"
              />
            </Center>
          </Link>
        </Box>
      </Container>
      <Container mb={200}></Container>
    </div>
  );
}

interface ParamProps {
  params: {
    slug: string;
  };
}

export async function getStaticProps({ params }: ParamProps) {
  const { slug } = params;
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: ["artist", slug],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/artists/${slug}/`);
      return res.json();
    },
  });

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 86400,
  };
}

export async function getStaticPaths() {
  const endpoint = `${apiUrl}/artists/`;

  const response = await fetch(endpoint);
  const artists = await response.json();

  const paths = await artists.map((artist: Artist) => ({
    params: { slug: artist.slug },
  }));

  return { paths, fallback: "blocking" };
}
